<template>
  <div class="school-users text-dark">
    <div class="school-users__header">
      <div class="school-users__header__text">
        <h1>{{ $t('vue2.school_users.title') }}</h1>
        <p v-if="isDK" class="do-pill do-pill--yellow school-users__intro">
          {{ $t('vue2.school_users.stil_intro') }}
          <a href="https://viden.stil.dk/pages/viewpage.action?pageId=161059151" target="_blank" rel="noopener noreferrer">{{ $t('vue2.school_users.stil_read_more') }}</a>
        </p>
      </div>
      <div class="school-users__header__actions">
        <a v-if="isDK" class="do-bttn" :href="stilImportUrl">{{ $t('vue2.school_users.stil_import') }}</a>
        <a class="do-bttn" :href="mergeUsersUrl">{{ $t('vue2.school_users.merge_users') }}</a>
        <a class="do-bttn" :href="transferCalendarUrl">{{ $t('vue2.school_users.transfer_calendar') }}</a>
        <button class="do-bttn do-bttn--submit" @click="openCreate">{{ $t('vue2.school_users.create') }}</button>
      </div>
    </div>

    <div class="school-users__toolbar">
      <input type="search" class="d-input school-users__search" v-model="search" :placeholder="$t('vue2.school_users.search_placeholder')">
      <span class="school-users__count" v-if="!isLoading">{{ countLabel }}</span>
    </div>

    <Loader v-if="isLoading"></Loader>
    <table v-else class="do-table do-table--striped school-users__table">
      <thead>
        <tr>
          <th class="school-users__cell--photo"></th>
          <th class="school-users__cell--initials school-users__sortable" @click="setSort('initials')">
            {{ $t('vue2.school_users.initials') }} <span class="school-users__sort">{{ sortIndicator('initials') }}</span>
          </th>
          <th class="school-users__cell--name school-users__sortable" @click="setSort('name')">
            {{ $t('vue2.school_users.name') }} <span class="school-users__sort">{{ sortIndicator('name') }}</span>
          </th>
          <th class="school-users__cell--login">{{ $t('vue2.school_users.login') }}</th>
          <th class="school-users__cell--phone">{{ $t('vue2.school_users.phone') }}</th>
          <th class="school-users__cell--seen school-users__sortable" @click="setSort('last_seen')">
            {{ $t('vue2.school_users.last_seen') }} <span class="school-users__sort">{{ sortIndicator('last_seen') }}</span>
          </th>
          <th class="school-users__cell--actions"></th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="visibleUsers.length === 0">
          <td colspan="7" class="italic">{{ search ? $t('vue2.school_users.no_results') : $t('vue2.school_users.none') }}</td>
        </tr>
        <tr v-for="user in visibleUsers" :key="user.id" class="school-users__row" @click="openEdit(user)">
          <td class="school-users__cell--photo">
            <img :src="user.default_thumb_url" :alt="user.name" class="school-users__avatar" width="36" height="36">
          </td>
          <td class="school-users__cell--initials">{{ user.initials }}</td>
          <td class="school-users__cell--name">
            <span class="school-users__name">{{ user.name }}</span>
            <span v-if="user.employee_id" class="school-users__sub">{{ $t('vue2.school_users.employee_id') }} {{ user.employee_id }}</span>
          </td>
          <td class="school-users__cell--login">
            <div v-for="login in logins(user)" :key="login.type" class="school-users__login">
              <span class="school-users__login__type">{{ login.type }}</span> {{ login.value }}
            </div>
          </td>
          <td class="school-users__cell--phone">{{ formatPhone(user.phone) }}</td>
          <td class="school-users__cell--seen">
            <span :class="{'school-users__never': !user.last_seen}">{{ lastSeen(user) }}</span>
          </td>
          <td class="school-users__cell--actions" @click.stop>
            <DDropdown :isShowDots="false" :isBottomStart="false" :appendToBody="false" :propOffset="[0, 4]" :closeFromProp="closeActionsMenu">
              <button slot="title" type="button" class="do-bttn do-bttn--small">
                {{ $t('vue2.school_users.actions') }} <span class="icon-arrow-down5"></span>
              </button>
              <ul slot="list">
                <li v-for="action in actions" :key="action.key" @click="runAction(action, user)">
                  <span :class="action.icon"></span> {{ action.label }}
                </li>
              </ul>
            </DDropdown>
          </td>
        </tr>
      </tbody>
    </table>

    <SchoolUserModal
      v-if="editingUser"
      :schoolId="schoolId"
      :user="editingUser"
      @saved="onSaved"
      @imageChanged="readList"
      @close="editingUser = null">
    </SchoolUserModal>
  </div>
</template>

<script>
import moment from 'moment'
import eventbus from '../../eventbus'
import docendo from '../../docendo'
import Loader from '../common/Loader'
import DDropdown from '../common/DDropdown'
import SchoolUserModal from './SchoolUserModal'
import { formatPhone } from './phone'

const LOGIN_TYPES = ['email', 'unilogin', 'wayf', 'feide']

export default {
  name: 'school-users',
  components: {Loader, DDropdown, SchoolUserModal},
  data () {
    return {
      isLoading: true,
      users: [],
      search: '',
      sortKey: 'name',
      sortAsc: true,
      editingUser: null,
      closeActionsMenu: 0
    }
  },
  computed: {
    schoolId () {
      return this.$store.getters.schoolId
    },
    isDK () {
      return (this.$store.getters.schoolCountry || '').toUpperCase() === 'DK'
    },
    stilImportUrl () {
      return '/schools/' + this.schoolId + '/stil_users_import/edit'
    },
    mergeUsersUrl () {
      return '/schools/' + this.schoolId + '/user_merges/new'
    },
    transferCalendarUrl () {
      return '/schools/' + this.schoolId + '/calendar_transfers/new'
    },
    createdId () {
      return eventbus.createdId
    },
    actions () {
      return [
        {key: 'edit', icon: 'icon-pencil', label: this.$t('vue2.school_users.edit_action'), run: this.openEdit}
      ]
    },
    filteredUsers () {
      const query = this.search.trim().toLowerCase()
      if (!query) {
        return this.users
      }
      return this.users.filter(user => {
        return ['name', 'initials', 'email', 'unilogin', 'feide', 'wayf', 'phone', 'employee_id']
          .some(key => user[key] && String(user[key]).toLowerCase().indexOf(query) > -1)
      })
    },
    visibleUsers () {
      const key = this.sortKey
      const direction = this.sortAsc ? 1 : -1
      return this.filteredUsers.slice().sort((a, b) => {
        const valueA = a[key] || ''
        const valueB = b[key] || ''
        if (key === 'last_seen') {
          return (moment(valueA || 0).valueOf() - moment(valueB || 0).valueOf()) * direction
        }
        return String(valueA).localeCompare(String(valueB), 'da', {numeric: true, sensitivity: 'base'}) * direction
      })
    },
    countLabel () {
      if (this.filteredUsers.length === this.users.length) {
        return this.users.length + ' ' + this.$t('vue2.school_users.users')
      }
      return this.filteredUsers.length + ' / ' + this.users.length + ' ' + this.$t('vue2.school_users.users')
    }
  },
  watch: {
    createdId () {
      this.readList()
    }
  },
  mounted () {
    this.readList()
  },
  methods: {
    readList () {
      if (!this.schoolId) {
        return
      }
      docendo.schoolUsers2(this.schoolId, data => {
        this.users = Array.isArray(data) ? data : []
        this.isLoading = false
      }, () => {
        this.isLoading = false
        eventbus.toast = {text: this.$t('vue2.school_users.error_toast'), success: false}
      })
    },
    setSort (key) {
      if (this.sortKey === key) {
        this.sortAsc = !this.sortAsc
      } else {
        this.sortKey = key
        this.sortAsc = key !== 'last_seen'
      }
    },
    sortIndicator (key) {
      if (this.sortKey !== key) {
        return ''
      }
      return this.sortAsc ? '▲' : '▼'
    },
    logins (user) {
      return LOGIN_TYPES
        .filter(type => user[type])
        .map(type => ({type: type, value: user[type]}))
    },
    formatPhone (phone) {
      return formatPhone(phone)
    },
    lastSeen (user) {
      if (!user.last_seen) {
        return this.$t('vue2.school_users.never_logged_in')
      }
      return moment(user.last_seen).fromNow()
    },
    runAction (action, user) {
      this.closeActionsMenu++
      action.run(user)
    },
    openCreate () {
      eventbus.createUserModal = true
    },
    openEdit (user) {
      this.editingUser = user
    },
    onSaved () {
      this.editingUser = null
      this.readList()
    }
  }
}
</script>

<style scoped>
@import "../../assets/css/variables.css";

.school-users {
  margin: 20px;
}

.school-users__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 2rem;
}

.school-users__header__text h1 {
  margin: 0 0 0.5rem;
}

.school-users__intro {
  margin: 0 0 0.75rem;
  line-height: 1.4;
  padding: 0.25rem 0.5rem;
  display: inline-block;
  font-size: 0.8rem;
  text-transform: none;
  max-width: 700px;
}

.school-users__header__actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 0.75rem;
}

.school-users__header__actions a.do-bttn {
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.school-users__header .do-bttn--submit {
  padding: 0 2rem;
  height: 36px;
}

.school-users__toolbar {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin: 1rem 0 0.75rem;
}

.school-users__search {
  width: 320px;
  max-width: 100%;
  height: 36px;
}

.school-users__count {
  font-size: 12px;
  color: var(--grey);
}

/* do-table gør hver række til sin egen tabel; her skal kolonnerne flugte på tværs af rækker. */
.school-users__table {
  table-layout: fixed;
}

.school-users__table thead {
  display: table-header-group;
}

.school-users__table tbody tr {
  display: table-row;
}

.school-users__table .italic {
  font-style: italic;
  color: var(--grey);
}

.school-users__row {
  cursor: pointer;
  transition: background-color 0.15s;
}

.school-users__table td {
  vertical-align: middle;
}

.school-users__sortable {
  cursor: pointer;
  user-select: none;
}

.school-users__sort {
  font-size: 8px;
}

.school-users__cell--photo {
  width: 56px;
}

.school-users__cell--initials {
  width: 80px;
}

.school-users__cell--name {
  width: 25%;
}

.school-users__cell--phone {
  width: 130px;
}

.school-users__cell--seen {
  width: 130px;
}

.school-users__cell--actions {
  width: 120px;
  text-align: right;
}

.school-users__cell--actions .d-dropdown-container {
  display: inline-block;
}

.school-users__cell--actions .do-bttn--small {
  font-size: 11px;
  height: auto;
  padding: 5px 8px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}

.school-users__avatar {
  border-radius: 50%;
  display: block;
  object-fit: cover;
  overflow: hidden;
  font-size: 0;
  background: #e2e8f0;
}

.school-users__name {
  font-weight: bold;
  display: block;
}

.school-users__sub,
.school-users__login__type,
.school-users__never {
  font-variant-caps: all-small-caps;
  color: var(--grey);
}

.school-users__login {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>



// WEBPACK FOOTER //
// SchoolUsers.vue?6584af26